<template>
  <div>
    <div class="tw-font-bold tw-mb-2">
      Symbol Proxies
    </div>
    <p class="tw-text-sm tw-text-gray-500 tw-mb-2">
      Map symbols not in our system to a proxy ticker for optimization. If the real symbol exists in our system after optimization, it will be substituted back.
    </p>

    <!-- Cash symbols: implicitly resolved to CASH_USD, but overrideable -->
    <div
      v-if="cashUnresolved.length > 0"
      class="tw-mb-3 tw-p-3 tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded"
    >
      <div class="tw-font-medium tw-text-blue-800 tw-mb-1">
        Cash positions (mapped to CASH_USD by default):
      </div>
      <div class="tw-flex tw-flex-col tw-gap-1">
        <div
          v-for="symbol in cashUnresolved"
          :key="symbol"
          class="tw-flex tw-items-center tw-gap-2 tw-text-sm"
        >
          <span class="tw-font-mono tw-bg-blue-100 tw-text-blue-800 tw-px-2 tw-py-0.5 tw-rounded">{{ symbol }}</span>
          <span class="tw-text-gray-400">→</span>
          <span
            v-if="proxyFor(symbol)"
            class="tw-font-mono tw-font-medium tw-text-gray-800"
          >{{ proxyFor(symbol) }}</span>
          <span
            v-else
            class="tw-font-mono tw-text-gray-500"
          >CASH_USD <span class="tw-text-xs tw-text-gray-400">(default)</span></span>
          <button
            class="tw-bg-transparent tw-border tw-border-gray-300 tw-rounded tw-px-2 tw-py-0.5 tw-text-xs tw-text-gray-600 tw-cursor-pointer hover:tw-bg-gray-100"
            @click="addProxyForSymbol(symbol)"
          >
            {{ proxyFor(symbol) ? 'Change proxy' : 'Override' }}
          </button>
          <button
            v-if="proxyFor(symbol)"
            class="tw-bg-transparent tw-border-none tw-text-xs tw-text-gray-400 hover:tw-text-red-500 tw-cursor-pointer"
            title="Remove override, revert to CASH_USD"
            @click="removeProxyForSymbol(symbol)"
          >
            <i class="fa fa-times" /> revert
          </button>
        </div>
      </div>
    </div>

    <!-- Non-cash unresolved symbols -->
    <div
      v-if="unprociedUnresolved.length > 0"
      class="tw-mb-3 tw-p-3 tw-bg-yellow-50 tw-border tw-border-yellow-300 tw-rounded"
    >
      <div class="tw-font-medium tw-text-yellow-800 tw-mb-1">
        Symbols not in our system (no proxy defined):
      </div>
      <div class="tw-flex tw-flex-wrap tw-gap-2">
        <span
          v-for="symbol in unprociedUnresolved"
          :key="symbol"
          class="tw-bg-yellow-100 tw-text-yellow-800 tw-px-2 tw-py-0.5 tw-rounded tw-text-sm tw-font-mono tw-cursor-pointer"
          title="Click to add a proxy for this symbol"
          @click="addProxyForSymbol(symbol)"
        >
          {{ symbol }}
        </span>
      </div>
      <p class="tw-text-xs tw-text-yellow-700 tw-mt-1">
        Click a symbol to add a proxy for it.
      </p>
    </div>

    <button
      class="tw-bg-blue-500 tw-text-white tw-px-2 tw-py-1 tw-rounded"
      @click="addProxy"
    >
      + Add Proxy
    </button>
    <div
      v-for="(proxy, index) in checkupStore.symbolProxies"
      :key="index"
      class="tw-mt-2 tw-flex tw-items-center tw-gap-2"
    >
      <input
        v-model="proxy.originalSymbol"
        class="tw-p-1 tw-border tw-rounded tw-uppercase"
        placeholder="Real symbol"
        @input="proxy.originalSymbol = proxy.originalSymbol.toUpperCase()"
      >
      <span class="tw-text-gray-500">→</span>
      <input
        v-model="proxy.proxySymbol"
        class="tw-p-1 tw-border tw-rounded tw-uppercase"
        placeholder="Proxy symbol"
        @input="proxy.proxySymbol = proxy.proxySymbol.toUpperCase()"
      >
      <button
        class="tw-bg-red-500 tw-text-white tw-px-2 tw-py-1 tw-rounded"
        @click="removeProxy(index)"
      >
        Remove
      </button>
    </div>
    <hr>
  </div>
</template>


<script>
import { mapStores } from 'pinia';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';
import { CASH_USD_SYMBOLS } from '@frontend/ui/Constants';

export default {
  name: 'SymbolProxySettings',
  computed: {
    ...mapStores(useCheckupStore),
    proxiedSymbols() {
      return new Set(
        this.checkupStore.symbolProxies
          .map(p => p.originalSymbol.toUpperCase())
          .filter(s => s)
      );
    },
    // All unresolved symbols that are known cash symbols (shown in the cash section)
    cashUnresolved() {
      return this.checkupStore.unresolvedTaxLotSymbols.filter(
        s => CASH_USD_SYMBOLS.has(s.toUpperCase())
      );
    },
    // Non-cash unresolved symbols that have no proxy defined (shown in the yellow warning)
    unprociedUnresolved() {
      return this.checkupStore.unresolvedTaxLotSymbols.filter(
        s => !CASH_USD_SYMBOLS.has(s.toUpperCase()) && !this.proxiedSymbols.has(s.toUpperCase())
      );
    },
  },
  methods: {
    proxyFor(symbol) {
      const entry = this.checkupStore.symbolProxies.find(
        p => p.originalSymbol.toUpperCase() === symbol.toUpperCase()
      );
      return entry ? entry.proxySymbol : null;
    },
    addProxy() {
      this.checkupStore.symbolProxies.push({ originalSymbol: '', proxySymbol: '' });
    },
    addProxyForSymbol(symbol) {
      const existing = this.checkupStore.symbolProxies.find(
        p => p.originalSymbol.toUpperCase() === symbol.toUpperCase()
      );
      if (!existing) {
        this.checkupStore.symbolProxies.push({ originalSymbol: symbol, proxySymbol: '' });
      }
    },
    removeProxy(index) {
      this.checkupStore.symbolProxies.splice(index, 1);
    },
    removeProxyForSymbol(symbol) {
      const index = this.checkupStore.symbolProxies.findIndex(
        p => p.originalSymbol.toUpperCase() === symbol.toUpperCase()
      );
      if (index !== -1) {
        this.checkupStore.symbolProxies.splice(index, 1);
      }
    },
  },
};
</script>
